/* eslint-disable */
import React from "react";
import { Skeleton } from "@material-ui/lab";
import { CalendarHierarchy, InputCalendarHierarchyItem } from "../../../controls/CalendarHierarchy";
import { useDictionary, useParameter } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { equal } from "../../../../../shared";
import { SLIDE_NUMBER } from "../constants";
import { getDateParts, getDateWithoutFrequency } from "../utils/dateHelpers";

export const CalendarHierarchyControl: React.FC = () => {
  const [controlItems, setControlItems] = React.useState<InputCalendarHierarchyItem[] | undefined>();
  const [dictItems] = useDictionary(dictionaryService, "calendar-origin");
  const [controlValue, setControlValue] = React.useState<number[]>([]);
  const [commodities] = useParameter<number[]>("@commodities");
  const [calendarValue, setCalendarValue] = useParameter<number[]>("@calendar");
  const [calendarDisplayValue, setCalendarDisplayValue] = useParameter<number[]>("@calendar_display");
  const [yearFrequency, monthFrequency, weekFrequency] = [1, 4, 7];

  const [dictItemsLastCalendar, , isLoadingDictItemsLastCalendar] = useDictionary(
    dictionaryService,
    "slides-f-last-calendar",
    undefined,
    {
      commodities: commodities ? commodities.join(",") : "120,12,13,18",
      slide: SLIDE_NUMBER,
      attributes: "",
      unit: "",
      countries: "",
    },
  );

  const [minAvailableWeek, maxAvailableWeek] = React.useMemo(() => {
    if (dictItemsLastCalendar != null && dictItemsLastCalendar.length > 0) {
      return [Number(dictItemsLastCalendar[0].id), Number(dictItemsLastCalendar[1].id)];
    }

    return [];
  }, [dictItemsLastCalendar]);

  React.useEffect(
    function initControlValue() {
      if (calendarValue?.length && controlValue.length === 0 && dictItems?.length) {
        const value = calendarValue.reduce((result, calendarId) => {
          const calendarFrequency = Number(String(calendarId)[0]);
          const [isWeek] = [weekFrequency].map((frequency) => frequency === calendarFrequency);
          if (isWeek && !result.includes(calendarId)) {
            result.push(calendarId);
          }
          return result;
        }, [] as number[]);
        setControlValue(value);
      }
    },
    [calendarValue, controlValue, dictItems],
  );

  React.useEffect(
    function updateStateValueByParameter() {
      if (calendarValue && !equal(calendarValue, controlValue)) {
        setControlValue(calendarValue);
      }
    },
    [calendarValue],
  );

  React.useEffect(
    function updateControlsItemsByDictionaryItems() {
      if (minAvailableWeek != null && maxAvailableWeek != null) {
        const candidatesControlItems = dictItems?.map<InputCalendarHierarchyItem & { frequency: number }>((item) => ({
          id: Number(item.id),
          parent: item.getValue<number>("parent"),
          name: item.getValue<string>("name") || "",
          frequency: item.getValue<number>("frequencyId")!,
        }));

        const { year } = getDateParts(minAvailableWeek);
        const minAvailableYear = Number(`1${year}0101`);

        const datesInRangeLimit = (candidatesControlItems || []).filter((item) => {
          return (
            getDateWithoutFrequency(item.id as number) >= getDateWithoutFrequency(minAvailableYear) &&
            getDateWithoutFrequency(item.id as number) <= getDateWithoutFrequency(maxAvailableWeek)
          );
        });
        const nextControlItems = datesInRangeLimit.reduce((result, item) => {
          if (item.frequency === yearFrequency) {
            result.push(item);
          } else if (item.frequency === monthFrequency) {
            const parentQuartal = dictItems?.find((dictItem) => equal(+dictItem.id, item.parent));
            const parentYearId = parentQuartal?.getValue<number>("parent")!;
            if (parentQuartal && parentYearId) {
              item.parent = parentYearId;
              result.push(item);
            }
          } else if (item.frequency === weekFrequency) {
            const paretMonth = dictItems?.find((dictItem) => equal(Number(dictItem.id), item.parent));
            if (paretMonth) {
              result.push(item);
            }
          }
          return result;
        }, [] as any);

        setControlItems(nextControlItems);
      }
    },
    [JSON.stringify(dictItems), dictItemsLastCalendar, minAvailableWeek, maxAvailableWeek],
  );

  const handlerChange = React.useCallback(
    (value: unknown) => {
      const monthesOrYearsIds = value as number[];
      if (!equal(calendarDisplayValue, monthesOrYearsIds) || !equal(calendarValue, monthesOrYearsIds)) {
        setCalendarDisplayValue(monthesOrYearsIds);
        setCalendarValue(monthesOrYearsIds);
      }
    },
    [dictItems],
  );
  const loading = isLoadingDictItemsLastCalendar;

  return loading ? (
    <Skeleton variant="rect" width="168px" height="51px" />
  ) : (
    <CalendarHierarchy label="Calendar" multiple value={controlValue} onChange={handlerChange} items={controlItems} />
  );
};
